import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { api, streamChat } from "@/lib/api";
import AppHeader from "@/components/AppHeader";
import VoiceOrb from "@/components/VoiceOrb";
import MessageBubble from "@/components/shared/MessageBubble";
import AudioFirstOverlay, { VoiceHelpAction } from "@/components/shared/AudioFirstOverlay";
import ChatComposer from "@/components/shared/ChatComposer";
import useVoiceSession from "@/hooks/useVoiceSession";
import useThreadAttach from "@/hooks/useThreadAttach";
import { docsFromMessages, messageFromAttach, promptToUseDoc } from "@/lib/attach";
import useStickyScroll from "@/hooks/useStickyScroll";
import { shouldCloseFullscreen } from "@/lib/voiceStage";
import { friendlyError } from "@/lib/errors";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
} from "@/components/ui/sheet";
import { CheckCircle2, ArrowRight, ArrowDown, Keyboard, ListChecks, Mic } from "lucide-react";
import { toast } from "sonner";

const BLOCKS = [
  { id: 1, label: "Cliente Real", fields: ["nome", "faixa_etaria", "nicho"] },
  { id: 2, label: "Contexto e Ocupação", fields: ["contexto_vida", "ocupacao"] },
  { id: 3, label: "Dores, Frustrações, Medos", fields: ["dores", "frustracoes", "medos"] },
  { id: 4, label: "Desejos", fields: ["desejos"] },
  { id: 5, label: "Objeções", fields: ["objecoes"] },
  { id: 6, label: "Comportamento", fields: ["comportamento"] },
  { id: 7, label: "Linguagem Real", fields: ["linguagem_real"] },
  { id: 8, label: "Matriz + Badges", fields: ["matriz_estrategica", "badges"] },
];

const FIELD_LABELS = {
  nome: "nome", faixa_etaria: "idade", nicho: "nicho", contexto_vida: "contexto",
  ocupacao: "ocupação", dores: "dores", frustracoes: "frustrações", medos: "medos",
  desejos: "desejos", objecoes: "objeções", comportamento: "comportamento",
  linguagem_real: "linguagem", matriz_estrategica: "matriz", badges: "badges",
};

export default function AvatarBuilder() {
  const { id } = useParams();
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const mode = searchParams.get("mode");
  const [thread, setThread] = useState(null);
  const [messages, setMessages] = useState([]);
  const [avatar, setAvatar] = useState(null);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [queued, setQueued] = useState(false);
  const [orbState, setOrbState] = useState("idle");
  const [showIntro, setShowIntro] = useState(true);
  const [historyReady, setHistoryReady] = useState(false);
  const [historyError, setHistoryError] = useState(false);
  const [loadAttempt, setLoadAttempt] = useState(0);
  const [checklistSheet, setChecklistSheet] = useState(false);
  const feed = useStickyScroll(messages);
  const initRef = useRef(false);
  // Mensagem digitada enquanto a IA responde: sai sozinha ao fim do stream.
  const queuedRef = useRef(null);
  const abortRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (initRef.current) return;
    initRef.current = true;
    setHistoryError(false);
    (async () => {
      try {
        const av = await api.get(`/avatars/${id}`);
        setAvatar(av.data);
        // Explicit modes use the inline start/resume controls, keeping history visible.
        if (mode) setShowIntro(false);
        const ts = await api.get("/threads", { params: { agent_slug: "avatar-builder" } });
        let t = (ts.data || []).find((x) => x.avatar_id === id);
        if (!t) {
          const created = await api.post("/threads", {
            agent_slug: "avatar-builder",
            avatar_id: id,
          });
          t = created.data;
        }
        setThread(t);
        const ms = await api.get(`/threads/${t.id}/messages`);
        if (ms.data.length === 0) {
          const ag = await api.get(`/agents/avatar-builder`);
          setMessages([{ id: "greet", role: "assistant", content: ag.data.greeting }]);
        } else {
          setMessages(ms.data);
          setShowIntro(false);
        }
        setHistoryReady(true);
      } catch (e) {
        // Only a genuine 404 means the avatar is gone — redirect to recreate it.
        // Network/5xx keeps the user here so a reload can recover.
        if (e?.response?.status === 404) {
          toast.error("Avatar não encontrado.");
          nav("/onboarding/avatar");
        } else {
          setHistoryError(true);
          toast.error(friendlyError(e));
        }
      }
    })();
  }, [id, nav, mode, loadAttempt]);

  const refreshAvatar = async () => {
    const { data } = await api.get(`/avatars/${id}`);
    setAvatar(data);
  };

  const handleVoiceEvent = (evt) => {
    if (evt.type === "transcript") {
      setMessages((m) => {
        const next = [...m];
        const last = next[next.length - 1];
        if (last && last.role === evt.role && last.live) {
          next[next.length - 1] = { ...last, content: last.content + evt.text };
        } else {
          next.push({ id: `v-${Date.now()}-${Math.random()}`, role: evt.role, content: evt.text, live: true });
        }
        return next;
      });
    } else if (evt.type === "turn_complete") {
      setMessages((m) => m.map((x) => (x.live ? { ...x, live: false } : x)));
      refreshAvatar().catch(() => {});
    } else if (evt.type === "tool_result") {
      // update_avatar refreshes the checklist in place — it must NOT close the stage.
      if (evt.name === "update_avatar" && evt.result?.ok) refreshAvatar().catch(() => {});
      // request_review is the only tool that ends the stage here: navigate away.
      if (shouldCloseFullscreen(evt).mode === "navigate") {
        endVoice();
        nav(`/avatars/${id}/review`);
      }
    } else if (evt.type === "error") {
      toast.error(evt.message || "A sessão de voz falhou. Tente novamente.");
    }
  };

  const voice = useVoiceSession({
    agent: "avatar-builder",
    threadId: thread?.id,
    onEvent: handleVoiceEvent,
  });

  const startVoice = () => {
    if (!thread?.id || !historyReady || voice.active || sending) return;
    setShowIntro(false);
    voice.dismissRecovery?.();
    voice.start();
  };
  const endVoice = () => { voice.stop(); };
  const continueVoiceByText = () => {
    voice.continueWithText?.();
    setShowIntro(false);
  };

  const attach = useThreadAttach({
    threadId: thread?.id,
    onAttached: (data) => {
      setShowIntro(false);
      setMessages((m) => [...m, messageFromAttach(data)]);
    },
  });

  const stopStreaming = () => {
    abortRef.current?.abort();
  };

  const detachDoc = async (doc) => {
    if (!thread?.id || !doc?.id) return;
    try {
      await api.delete(`/threads/${thread.id}/attach/${doc.id}`);
      setMessages((m) => m.filter((x) => x.id !== doc.id));
    } catch (err) {
      toast.error(friendlyError(err));
    }
  };

  const sendMessage = async (contentArg) => {
    const content = (contentArg ?? input).trim();
    if (!content || !thread) return;
    // Streaming em curso: enfileira em vez de descartar em silêncio.
    if (sending) {
      queuedRef.current = content;
      setQueued(true);
      if (contentArg == null) setInput("");
      return;
    }
    setInput("");
    setShowIntro(false);
    setMessages((m) => [...m, { id: `u-${Date.now()}`, role: "user", content }]);
    setMessages((m) => [...m, { id: `a-${Date.now()}`, role: "assistant", content: "" }]);
    setSending(true);
    setOrbState("thinking");
    const controller = new AbortController();
    abortRef.current = controller;
    try {
      await streamChat(thread.id, content, async (evt) => {
        if (evt.type === "token") {
          setOrbState("speaking");
          setMessages((m) => {
            const next = [...m];
            const i = next.length - 1;
            if (next[i]?.role === "assistant" && !next[i].closed) {
              next[i] = { ...next[i], content: next[i].content + evt.content };
              return next;
            }
            return [...next, { id: `a-${Date.now()}-${next.length}`, role: "assistant", content: evt.content }];
          });
        } else if (evt.type === "message_end") {
          setMessages((m) => {
            const next = [...m];
            const i = next.length - 1;
            if (next[i]?.role === "assistant") next[i] = { ...next[i], closed: true };
            return next;
          });
        } else if (evt.type === "tool_call") {
          setOrbState("tool");
        } else if (evt.type === "tool_result") {
          if (evt.name === "update_avatar" && evt.result?.ok) await refreshAvatar();
          if (evt.name === "request_review" && evt.result?.ok) {
            nav(`/avatars/${id}/review`);
          }
        } else if (evt.type === "error") {
          toast.error(evt.message || "A ValiosIA teve um problema ao responder. Tente de novo.");
          setOrbState("idle");
          setMessages((m) => m.filter((x) => !(x.role === "assistant" && !x.content)));
        } else if (evt.type === "done") {
          setOrbState("idle");
          refreshAvatar().catch(() => {});
        }
      }, controller.signal);
    } catch (e) {
      setMessages((m) => m.filter((x) => !(x.role === "assistant" && !x.content)));
      if (e?.name === "AbortError") {
        setOrbState("idle");
      } else {
        toast.error(friendlyError(e));
        setOrbState("error");
      }
    } finally {
      abortRef.current = null;
      setSending(false);
      const pending = queuedRef.current;
      if (pending) {
        queuedRef.current = null;
        setQueued(false);
        setTimeout(() => sendMessage(pending), 0);
      }
    }
  };

  const blockProgress = (block) => block.fields.every((f) => avatar && avatar[f]);
  const filledBlocks = BLOCKS.filter(blockProgress).length;

  const canReview = avatar &&
    ["nome", "faixa_etaria", "nicho", "contexto_vida", "dores", "frustracoes",
     "medos", "desejos", "objecoes", "comportamento", "linguagem_real",
     "matriz_estrategica"].every((f) => avatar[f]);

  const introVisible = showIntro && !voice.recovery && historyReady && thread && messages.length <= 1 && !voice.active;
  const hasConversation = messages.some((m) => m.role === "user");
  const voiceActionLabel = voice.status === "error"
    ? "Tentar voz novamente"
    : hasConversation ? "Retomar conversa por voz" : "Iniciar conversa por voz";
  const orbActive = voice.active ? voice.status : orbState;

  const checklist = (
    <>
      <div className="h-1.5 rounded-full bg-brand-elevated mb-4 overflow-hidden">
        <div
          className="h-full bg-brand-rose rounded-full transition-all duration-500"
          style={{ width: `${(filledBlocks / BLOCKS.length) * 100}%` }}
        />
      </div>
      <ol className="space-y-3">
        {BLOCKS.map((b) => {
          const done = blockProgress(b);
          const missing = b.fields.filter((f) => !avatar?.[f]);
          return (
            <li key={b.id} data-testid={`block-status-${b.id}`} className="flex items-start gap-3">
              <span
                className={`w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-ui shrink-0 ${
                  done ? "bg-brand-sage text-white" : "bg-brand-elevated text-brand-ink2"
                }`}
              >
                {done ? <CheckCircle2 className="w-3.5 h-3.5" /> : b.id}
              </span>
              <span className="min-w-0">
                <span className={`text-[14px] block ${done ? "text-brand-ink" : "text-brand-ink2"}`}>
                  {b.label}
                </span>
                {!done && missing.length > 0 && (
                  <span className="text-[10.5px] text-brand-sand font-ui">
                    falta: {missing.map((f) => FIELD_LABELS[f] || f).join(", ")}
                  </span>
                )}
              </span>
            </li>
          );
        })}
      </ol>
      {canReview && (
        <button
          data-testid="open-review"
          onClick={() => nav(`/avatars/${id}/review`)}
          className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-brand-rose hover:bg-brand-roseHover text-white px-5 py-3 rounded-full text-[13px] font-ui transition-colors min-h-[44px]"
        >
          Abrir tela de revisão
          <ArrowRight className="w-4 h-4" />
        </button>
      )}
    </>
  );

  return (
    <div className="h-dvh bg-brand-bg flex flex-col overflow-hidden" data-testid="avatar-builder-page">
      <AppHeader />
      <div className="flex-1 min-h-0 min-w-0 w-full max-w-7xl mx-auto px-3 sm:px-6 lg:px-10 pt-3 lg:pt-5 pb-3 grid grid-rows-1 grid-cols-1 lg:grid-cols-[minmax(0,0.85fr)_minmax(0,1.4fr)] gap-4 lg:gap-8">
        {/* Checklist — desktop own scroll column */}
        <aside className="hidden lg:flex flex-col min-h-0 min-w-0 bg-brand-surface border border-brand-line rounded-3xl p-5 lg:p-6" data-testid="avatar-checklist">
          <div className="mb-3 min-w-0">
            <div className="text-[11px] tracking-[0.22em] uppercase text-brand-ink2 font-ui mb-1">
              Memória do Avatar
            </div>
            <div className="flex min-w-0 items-center justify-between gap-2">
              <h2 className="min-w-0 flex-1 font-display text-xl lg:text-2xl text-brand-ink truncate" title={avatar?.nome || "Sua cliente ideal"}>
                {avatar?.nome || "Sua cliente ideal"}
              </h2>
              <span className="text-[12px] text-brand-rose font-ui shrink-0" data-testid="avatar-block-progress">
                {filledBlocks}/{BLOCKS.length}
              </span>
            </div>
          </div>
          <div className="min-h-0 overflow-y-auto overflow-x-hidden scrollbar-soft -mr-2 pr-2">
            {checklist}
          </div>
        </aside>

        {/* Chat */}
        <div className="relative bg-brand-surface border border-brand-line rounded-3xl flex flex-col min-h-0 min-w-0 overflow-hidden">
          <div className="flex items-center gap-3 lg:gap-4 px-4 lg:px-7 py-3.5 lg:py-4 border-b border-brand-line">
            <VoiceOrb
              state={orbActive}
              size={48}
              levels={voice.active ? voice.levels : null}
              showLabel={false}
            />
            <div className="flex-1 min-w-0">
              <div className="font-display text-lg text-brand-ink truncate">Avatar Builder</div>
              <div className="text-[12px] text-brand-ink2 font-ui truncate">
                {voice.active ? "conversa por voz ao vivo ✦" : "voz · texto · documentos"}
              </div>
            </div>
            {/* Mobile: open checklist */}
            <button
              type="button"
              data-testid="checklist-toggle"
              onClick={() => setChecklistSheet(true)}
              className="lg:hidden inline-flex items-center gap-1.5 px-3 py-2 rounded-full bg-brand-elevated border border-brand-line text-[12px] text-brand-ink2 hover:text-brand-rose transition-colors min-h-[44px]"
            >
              <ListChecks className="w-3.5 h-3.5" />
              {filledBlocks}/{BLOCKS.length}
            </button>
            {voice.active && (
              <button
                data-testid="prefer-text"
                onClick={endVoice}
                className="hidden sm:inline-flex items-center gap-1.5 text-[12px] text-brand-ink2 hover:text-brand-rose font-ui transition-colors min-h-[44px] px-2"
              >
                <Keyboard className="w-3.5 h-3.5" />
                prefiro digitar
              </button>
            )}
          </div>

          <div
            ref={feed.ref}
            onScroll={feed.onScroll}
            className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden px-4 lg:px-7 py-5 lg:py-6 space-y-4 scrollbar-soft"
            data-testid="avatar-chat-feed"
          >
            {messages.map((m) => (
              <MessageBubble key={m.id} message={m} />
            ))}
          </div>

          {!feed.atBottom && (
            <button
              type="button"
              data-testid="scroll-to-latest"
              onClick={() => feed.scrollToBottom()}
              className="absolute left-1/2 -translate-x-1/2 bottom-24 inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full bg-brand-elevated border border-brand-line text-[12px] text-brand-ink shadow-lg hover:border-brand-rose transition-colors z-10"
            >
              <ArrowDown className="w-3.5 h-3.5 text-brand-rose" /> novas mensagens
            </button>
          )}

          {!voice.active && !introVisible && (
            <div className="shrink-0 border-t border-brand-line px-4 lg:px-7 py-2" data-testid="avatar-voice-actions">
              <p role="status" className="text-[12px] text-brand-ink2 font-ui mb-2">
                {historyError ? "Não foi possível carregar sua conversa. Tente novamente para recuperar seu progresso."
                  : !historyReady ? "Carregando sua conversa..."
                  : voice.status === "error" ? "Não foi possível iniciar a voz. Tente novamente ou continue digitando."
                  : hasConversation ? "Seu progresso está salvo. Continue por voz ou texto."
                  : "Toque para iniciar a voz ou escreva sua resposta abaixo."}
              </p>
              <div className="flex flex-wrap gap-2">
                {historyError && (
                  <button type="button" data-testid="avatar-retry-history"
                    onClick={() => { initRef.current = false; setLoadAttempt((n) => n + 1); }}
                    className="rounded-full border border-brand-line text-brand-ink px-4 py-2 text-[13px] font-ui min-h-[44px]">
                    Recarregar conversa
                  </button>
                )}
                <button type="button" data-testid="avatar-start-voice" onClick={startVoice}
                  disabled={!historyReady || !thread?.id || sending}
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-brand-rose hover:bg-brand-roseHover text-white px-4 py-2 text-[13px] font-ui min-h-[44px] disabled:opacity-50">
                  <Mic className="w-4 h-4" />{voiceActionLabel}
                </button>
                <button type="button" data-testid="avatar-use-text" disabled={!historyReady || !thread?.id}
                  onClick={() => { setShowIntro(false); inputRef.current?.focus(); }}
                  className="inline-flex items-center justify-center gap-2 rounded-full border border-brand-line text-brand-ink px-4 py-2 text-[13px] font-ui min-h-[44px] disabled:opacity-50">
                  <Keyboard className="w-4 h-4" />Digitar
                </button>
              </div>
            </div>
          )}

          <ChatComposer
            inputRef={inputRef}
            value={input}
            onChange={setInput}
            onSend={() => sendMessage()}
            onStop={stopStreaming}
            sending={sending}
            queued={queued}
            placeholder="Responda com profundidade..."
            onFiles={thread ? attach.attachFiles : undefined}
            attachItems={attach.items}
            threadDocs={docsFromMessages(messages)}
            onUseDoc={(doc) => sendMessage(promptToUseDoc(doc.filename))}
            onDetachDoc={detachDoc}
            attaching={attach.attaching}
            fileInputRef={attach.inputRef}
            onMicToggle={() => (voice.active ? endVoice() : startVoice())}
            voiceActive={voice.active}
            voiceStatus={voice.status}
            micDisabled={sending}
            disabled={!thread || !historyReady}
            testids={{ input: "avatar-input", send: "avatar-send", attach: "attach-btn", voice: "voice-toggle" }}
          />
          {voice.active && !voice.recovery && (
            <VoiceHelpAction onClick={voice.reportAudioProblem} />
          )}

          {voice.recovery && (
            <AudioFirstOverlay
              title={voice.recovery.title}
              description={voice.recovery.message}
              onVoice={voice.resume}
              onText={continueVoiceByText}
              voiceLabel={voice.recovery.actionLabel || "Retomar voz"}
              textLabel="Continuar por texto"
              recovery
            />
          )}

          {introVisible && (
            <AudioFirstOverlay
              title="Vamos criar seu avatar conversando?"
              description="É uma conversa contínua, não uma gravação. A ValiosIA te guia ao vivo. Troca para texto ou anexa um documento a qualquer momento."
              onVoice={startVoice}
              onText={() => setShowIntro(false)}
              onAttach={() => { setShowIntro(false); attach.openPicker(); }}
            />
          )}
        </div>
      </div>

      {/* Mobile checklist sheet */}
      <Sheet open={checklistSheet} onOpenChange={setChecklistSheet}>
        <SheetContent side="bottom" data-testid="checklist-sheet" className="bg-brand-surface border-brand-line rounded-t-3xl max-h-[85vh] overflow-y-auto overflow-x-hidden scrollbar-soft">
          <SheetHeader className="text-left">
            <SheetTitle className="font-display text-2xl text-brand-ink">
              {avatar?.nome || "Sua cliente ideal"}
            </SheetTitle>
          </SheetHeader>
          <div className="mt-4">
            {checklist}
          </div>
        </SheetContent>
      </Sheet>

    </div>
  );
}
